import React, { useMemo } from 'react';
import { extractTrackSegments, buildEntryForSegment } from '../utils/splitTimes';
import { formatSpeed } from '../utils/format';

const TRACE_COLORS = [
  '#2e86c1', '#e74c3c', '#27ae60', '#f39c12', '#9b59b6',
  '#1abc9c', '#e67e22', '#3498db', '#e91e63', '#00bcd4',
];

const styles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  card: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    overflowX: 'auto',
  },
  empty: {
    padding: '18px 20px',
    color: 'var(--color-text-dim)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    border: '1px solid var(--color-border)',
    borderRadius: 10,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 600,
  },
  th: {
    padding: '10px 12px',
    background: 'var(--color-surface-light)',
    borderBottom: '1px solid var(--color-border)',
    color: 'var(--color-text-dim)',
    fontFamily: 'var(--font-heading)',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '10px 12px',
    borderBottom: '1px solid var(--color-border)',
    color: 'var(--color-text)',
    fontFamily: 'var(--font-data)',
    fontSize: 13,
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  lapCell: {
    fontFamily: 'var(--font-heading)',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.5,
    color: 'var(--color-text)',
    position: 'sticky',
    left: 0,
    background: 'var(--color-surface)',
    zIndex: 1,
    minWidth: 70,
    whiteSpace: 'nowrap',
  },
  lapHeader: {
    textAlign: 'left',
    position: 'sticky',
    left: 0,
    zIndex: 2,
  },
  subHeader: {
    fontSize: 9,
    letterSpacing: 1,
  },
  best: {
    color: '#27ae60',
    fontWeight: 700,
  },
  worst: {
    color: '#e74c3c',
    opacity: 0.7,
  },
  lapSwatch: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: 2,
    marginRight: 6,
    verticalAlign: 'middle',
  },
  borderLeft: {
    borderLeft: '1px solid var(--color-border)',
  },
};

function formatTimeMs(ms) {
  if (ms == null || !isFinite(ms)) return '--';
  return (ms / 1000).toFixed(2) + 's';
}

export default function CornerBreakdown({ trackCoordinates, laps, user }) {
  const uniqueDrivers = new Set((laps || []).map((lap) => lap.driverLabel).filter(Boolean));
  const isCompareMode = uniqueDrivers.size > 1;
  const segments = useMemo(
    () => extractTrackSegments(trackCoordinates),
    [trackCoordinates]
  );

  const data = useMemo(() => {
    if (!segments.length || !laps?.length) return null;

    const lapEntries = laps.map((lap) => {
      const entries = segments.map((seg) => buildEntryForSegment(lap, seg, 200));
      return { lap, entries };
    });

    // Compute best/worst per segment per metric for highlighting
    const bestWorst = {};
    for (let si = 0; si < segments.length; si++) {
      const metrics = ['entrySpeedMph', 'minSpeedMph', 'exitSpeedMph', 'sectionTimeMs'];
      bestWorst[si] = {};
      for (const metric of metrics) {
        const values = lapEntries
          .map((le) => ({ val: le.entries[si]?.[metric] }))
          .filter((v) => v.val != null && isFinite(v.val));
        if (!values.length) continue;
        const isTime = metric === 'sectionTimeMs';
        const bestVal = isTime
          ? Math.min(...values.map((v) => v.val))
          : Math.max(...values.map((v) => v.val));
        const worstVal = isTime
          ? Math.max(...values.map((v) => v.val))
          : Math.min(...values.map((v) => v.val));
        bestWorst[si][metric] = { bestVal, worstVal };
      }
    }

    return { segments, lapEntries, bestWorst };
  }, [segments, laps]);

  if (!data || !data.segments.length) {
    return <div style={styles.empty}>No corner data available — track has no defined segments.</div>;
  }

  const { lapEntries, bestWorst } = data;

  function getCellStyle(segIdx, metric, value) {
    if (value == null || !isFinite(value)) return {};
    const bw = bestWorst[segIdx]?.[metric];
    if (!bw) return {};
    if (lapEntries.length < 2) return {};
    if (value === bw.bestVal && bw.bestVal !== bw.worstVal) return styles.best;
    if (value === bw.worstVal && bw.bestVal !== bw.worstVal) return styles.worst;
    return {};
  }

  const metrics = ['entrySpeedMph', 'minSpeedMph', 'exitSpeedMph', 'sectionTimeMs'];
  const metricLabels = { entrySpeedMph: 'Entry', minSpeedMph: 'Min', exitSpeedMph: 'Exit', sectionTimeMs: 'Time' };

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            {/* Top header: Lap label + segment names spanning 4 sub-columns each */}
            <tr>
              <th style={{ ...styles.th, ...styles.lapHeader }} rowSpan={2}>Lap</th>
              {segments.map((seg, si) => (
                <th key={si} style={{ ...styles.th, textAlign: 'center', ...(si > 0 ? styles.borderLeft : {}) }} colSpan={4}>
                  {seg.label}
                </th>
              ))}
            </tr>
            {/* Sub-header: Entry / Min / Exit / Time under each segment */}
            <tr>
              {segments.map((seg, si) => (
                <React.Fragment key={`sub-${si}`}>
                  {metrics.map((m, mi) => (
                    <th key={m} style={{ ...styles.th, ...styles.subHeader, ...(mi === 0 && si > 0 ? styles.borderLeft : {}) }}>
                      {metricLabels[m]}
                    </th>
                  ))}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {lapEntries.map((le, li) => (
              <tr key={li}>
                <td style={{ ...styles.td, ...styles.lapCell, textAlign: 'left' }}>
                  <span style={{ ...styles.lapSwatch, background: TRACE_COLORS[li % TRACE_COLORS.length] }} />
                  {isCompareMode && le.lap.driverLabel
                    ? `${le.lap.driverLabel} L${le.lap.lapNum}`
                    : `Lap ${le.lap.lapNum}`}
                </td>
                {segments.map((seg, si) => {
                  const entry = le.entries[si];
                  return (
                    <React.Fragment key={`${li}-${si}`}>
                      <td style={{ ...styles.td, ...(si > 0 ? styles.borderLeft : {}), ...getCellStyle(si, 'entrySpeedMph', entry?.entrySpeedMph) }}>
                        {entry ? formatSpeed(entry.entrySpeedMph, user) : '--'}
                      </td>
                      <td style={{ ...styles.td, ...getCellStyle(si, 'minSpeedMph', entry?.minSpeedMph) }}>
                        {entry ? formatSpeed(entry.minSpeedMph, user) : '--'}
                      </td>
                      <td style={{ ...styles.td, ...getCellStyle(si, 'exitSpeedMph', entry?.exitSpeedMph) }}>
                        {entry ? formatSpeed(entry.exitSpeedMph, user) : '--'}
                      </td>
                      <td style={{ ...styles.td, ...getCellStyle(si, 'sectionTimeMs', entry?.sectionTimeMs) }}>
                        {entry ? formatTimeMs(entry.sectionTimeMs) : '--'}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
